import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, X, ArrowRight } from "lucide-react";
import { GRAND_OPENING_DATE } from "../config";

// Currently announcing the Thanksgiving Turkey Basket Giveaway.
// After the event, restore the grand reopening copy and set
// GRAND_OPENING_DATE in config.js to countdown to the reopening.
const GIVEAWAY_DATE = "2026-11-25T10:00:00";
const DISMISS_KEY = "spintastic-banner-turkey";

export const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = Math.max(0, new Date(target).getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    done: diff <= 0,
  };
};

export default function GrandOpening() {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1"
  );
  const countdown = useCountdown(GRAND_OPENING_DATE || GIVEAWAY_DATE);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          data-testid="grand-opening-banner"
          initial={{ y: "-100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative bg-[#C85C40] text-[#F9F6F0]"
        >
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-4 gap-y-2 px-6 py-3.5 text-center lg:px-10">
            <Leaf className="h-4 w-4 shrink-0" aria-hidden="true" />
            <p className="text-sm font-bold tracking-wide">
              Together, we give thanks — our{" "}
              <span className="italic font-serif">Turkey Basket Giveaway</span>{" "}
              is Wednesday, November 25 at 10:00 AM!
            </p>

            {countdown && !countdown.done && (
              <div
                className="flex items-center gap-2"
                data-testid="grand-opening-countdown"
              >
                {[
                  [countdown.days, "days"],
                  [countdown.hours, "hrs"],
                  [countdown.minutes, "min"],
                  [countdown.seconds, "sec"],
                ].map(([v, label]) => (
                  <span
                    key={label}
                    className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold tabular-nums"
                  >
                    {String(v).padStart(2, "0")} {label}
                  </span>
                ))}
              </div>
            )}

            <a
              href="/community"
              data-testid="banner-learn-more-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] px-4 py-1.5 text-xs font-bold text-[#F9F6F0] transition-[background-color,transform] duration-300 hover:scale-[0.97] hover:bg-[#7D8C74]"
            >
              Learn More
              <ArrowRight className="h-3.5 w-3.5" />
            </a>

            <button
              onClick={dismiss}
              data-testid="grand-opening-dismiss-btn"
              aria-label="Dismiss announcement"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full p-1.5 transition-colors duration-300 hover:bg-white/15"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
